/**
 * Result of a successful geolocation acquisition.
 */
export interface ICapturedGeoLocation {
  latitude: number;
  longitude: number;
  label: string;
}

import * as Location from "expo-location";
import {
  checkLocationServicesEnabled,
  checkLocationPermissions,
  requestLocationPermissions,
} from "./location-permissions";

/**
 * Fetches the user's high-accuracy GPS coordinates via expo-location or fallback geolocation.
 * Checks permissions and verifies GPS is enabled.
 * @returns {Promise<ICapturedGeoLocation | null>} The acquired coordinates or null if unavailable/denied.
 */
export async function getHighAccuracyLocation(): Promise<ICapturedGeoLocation | null> {
  try {
    // 1. Check if location services (GPS hardware) are enabled
    const gpsEnabled = await checkLocationServicesEnabled();
    if (!gpsEnabled) {
      console.warn("[MediaCapture] Location services (GPS) are disabled on this device.");
      return null;
    }

    // 2. Ensure permissions are granted
    let perm = await checkLocationPermissions();
    if (!perm.granted) {
      perm = await requestLocationPermissions();
      if (!perm.granted) {
        console.warn("[MediaCapture] Location permission not granted.");
        return null;
      }
    }

    // 3. Acquire position via expo-location
    const pos = await Location.getCurrentPositionAsync({
      accuracy: Location.Accuracy.High,
      timeInterval: 5000,
      mayShowUserSettingsDialog: true,
    });

    const { latitude, longitude } = pos.coords;
    const latStr = latitude >= 0 ? `${latitude.toFixed(4)}°N` : `${Math.abs(latitude).toFixed(4)}°S`;
    const lonStr = longitude >= 0 ? `${longitude.toFixed(4)}°E` : `${Math.abs(longitude).toFixed(4)}°W`;
    return {
      latitude,
      longitude,
      label: `${latStr}, ${lonStr}`,
    };
  } catch (err: any) {
    console.warn("[MediaCapture] expo-location failed:", err?.message || err);

    // Fallback to browser navigator.geolocation if available
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const { latitude, longitude } = pos.coords;
            const latStr = latitude >= 0 ? `${latitude.toFixed(4)}°N` : `${Math.abs(latitude).toFixed(4)}°S`;
            const lonStr = longitude >= 0 ? `${longitude.toFixed(4)}°E` : `${Math.abs(longitude).toFixed(4)}°W`;
            resolve({
              latitude,
              longitude,
              label: `${latStr}, ${lonStr}`,
            });
          },
          (navErr) => {
            console.warn("[MediaCapture] navigator.geolocation fallback skipped:", navErr.message);
            resolve(null);
          },
          {
            enableHighAccuracy: true,
            timeout: 12000,
            maximumAge: 0,
          }
        );
      });
    }

    return null;
  }
}

/**
 * Generates an archival timestamped file name for newly captured media.
 * @param {"photo" | "video" | "audio"} type - The capture media mode.
 * @param {string} [mimeType] - The MIME content type.
 * @returns {string} Timestamped file name (e.g. Photo_2026-10-03_214500.jpg).
 */
export function generateCaptureFileName(type: "photo" | "video" | "audio", mimeType?: string): string {
  const now = new Date();
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const timeStr = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

  let ext = "jpg";
  if (type === "video") {
    ext = mimeType?.includes("mp4") ? "mp4" : "webm";
  } else if (type === "audio") {
    ext = mimeType?.includes("mp4") || mimeType?.includes("m4a") ? "m4a" : "webm";
  } else {
    ext = mimeType?.includes("png") ? "png" : "jpg";
  }

  const prefix = type.charAt(0).toUpperCase() + type.slice(1);
  return `${prefix}_${dateStr}_${timeStr}.${ext}`;
}

/**
 * Converts a raw binary Blob into a standard File object.
 * @param {Blob} blob - The recorded or captured binary Blob.
 * @param {string} fileName - File name to assign.
 * @param {string} contentType - MIME content type.
 * @returns {File} Instantiated File instance.
 */
export function blobToFile(blob: Blob, fileName: string, contentType: string): File {
  return new File([blob], fileName, {
    type: contentType,
    lastModified: Date.now(),
  });
}

/**
 * Returns stream constraints requesting the highest supported camera resolution.
 * @param {"photo" | "video"} mode - Camera capture mode.
 * @param {"environment" | "user"} [facingMode="environment"] - Camera facing direction.
 * @returns {MediaStreamConstraints} Constraints object for getUserMedia.
 */
export function getHighestResolutionConstraints(
  mode: "photo" | "video",
  facingMode: "environment" | "user" = "environment"
): MediaStreamConstraints {
  return {
    video: {
      width: { ideal: 4096, min: 1920 },
      height: { ideal: 2160, min: 1080 },
      facingMode: { ideal: facingMode },
    },
    audio: mode === "video" ? {
      echoCancellation: true,
      noiseSuppression: true,
      sampleRate: 48000,
    } : false,
  };
}

/**
 * Returns stream constraints for high-fidelity audio capture.
 * @returns {MediaStreamConstraints} Constraints object for audio getUserMedia.
 */
export function getHighFidelityAudioConstraints(): MediaStreamConstraints {
  return {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      sampleRate: 48000,
      channelCount: 2,
    },
    video: false,
  };
}
